import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const NFCCards = () => {
  const [selectedCard, setSelectedCard] = useState('wooden');
  const [quantities, setQuantities] = useState({
    wooden: 1,
    blackPlastic: 1,
    appStyle: 1
  });
  
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const cards = {
    wooden: {
      id: 'wooden',
      title: 'Eco Wooden Card',
      subtitle: 'Sustainable Elegance',
      description: 'Crafted from premium bamboo with unique grain patterns. Perfect for eco-conscious professionals who value sustainability.',
      features: [
        { icon: 'leaf', text: '100% Sustainable Bamboo' },
        { icon: 'fingerprint', text: 'Unique Natural Grain' },
        { icon: 'award', text: 'Premium Laser Engraving' },
        { icon: 'shield-alt', text: '5-Year Durability' }
      ],
      price: 19.99,
      originalPrice: 29.99,
      savings: 'Save 33%',
      image: '/assets/cardwooden.png',
      gradient: 'from-amber-500 to-amber-700',
      accent: 'bg-amber-500',
      popular: true,
      type: 'nfc-card'
    },
    blackPlastic: {
      id: 'blackPlastic',
      title: 'Executive Black',
      subtitle: 'Professional Excellence',
      description: 'Sleek matte black finish with premium feel. The ultimate choice for executives and corporate professionals.',
      features: [
        { icon: 'water', text: 'Waterproof Design' },
        { icon: 'gem', text: 'Premium Matte Finish' },
        { icon: 'bolt', text: 'Instant Recognition' },
        { icon: 'check-circle', text: 'Scratch Resistant' }
      ],
      price: 9.99,
      originalPrice: 19.99,
      savings: 'Save 50%',
      image: '/assets/cardblack.png',
      gradient: 'from-cyan-400 to-teal-600',
      accent: 'bg-gray-800',
      popular: false,
      type: 'nfc-card'
    },
    appStyle: {
      id: 'appStyle',
      title: 'Vibrant Gradient',
      subtitle: 'Modern Innovation',
      description: 'Eye-catching gradient design that matches our app. Stand out at every networking event with style.',
      features: [
        { icon: 'palette', text: 'Stunning Gradient Design' },
        { icon: 'paint-brush', text: 'High-Gloss Finish' },
        { icon: 'mobile-alt', text: 'App-Matching Colors' },
        { icon: 'star', text: 'Conversation Starter' }
      ],
      price: 12.99,
      originalPrice: 22.99,
      savings: 'Save 43%',
      image: '/assets/cardapp.png',
      gradient: 'from-blue-500 to-purple-600',
      accent: 'bg-gradient-to-r from-blue-500 to-purple-600',
      popular: false,
      type: 'nfc-card'
    }
  };

  const handleAddToCart = async (cardId) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    const card = cards[cardId];
    const quantity = quantities[cardId];
    
    const cartItem = {
      id: cardId,
      name: card.title,
      description: card.subtitle,
      price: card.price,
      quantity: quantity,
      image: card.image,
      type: card.type
    };

    try {
      await addToCart(cartItem);
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  const handleQuantityChange = (cardId, newQuantity) => {
    if (newQuantity >= 1) {
      setQuantities({
        ...quantities,
        [cardId]: newQuantity
      });
    }
  };

  const handleBuyNow = async (cardId) => {
    await handleAddToCart(cardId);
    navigate('/cart');
  };

  return (
    <section id="nfc-cards" className="py-12 sm:py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-blue-100 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-purple-100 rounded-full filter blur-3xl opacity-20 translate-x-1/2 translate-y-1/2"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
          <div className="badge badge-blue inline-flex mb-3 sm:mb-4">
            <FontAwesomeIcon icon="credit-card" />
            <span>Premium Cards</span>
          </div>
          
          <h2 className="text-h2 mb-4 sm:mb-6">
            Choose Your <span className="text-gradient-blue-pink">Perfect Card</span>
          </h2>
          
          <p className="text-body-lg text-gray-600 px-4 sm:px-0">
            Premium NFC cards that make sharing your digital profile as simple as a tap. 
            Sustainable, stylish, and incredibly smart.
          </p>
        </div>

        {/* Card Type Selector */}
        <div className="flex justify-center mb-8 sm:mb-12 md:mb-16">
          <div className="inline-flex bg-gray-100 p-1 sm:p-1.5 rounded-xl shadow-sm">
            <button
              className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg text-btn transition-all duration-300 ${
                selectedCard === 'wooden'
                  ? 'bg-white text-primary-blue shadow-md'
                  : 'text-gray-600 hover:text-dark'
              }`}
              onClick={() => setSelectedCard('wooden')}
            >
              Wooden
            </button>
            
            <button
              className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg text-btn transition-all duration-300 ${
                selectedCard === 'blackPlastic'
                  ? 'bg-white text-primary-blue shadow-md'
                  : 'text-gray-600 hover:text-dark'
              }`}
              onClick={() => setSelectedCard('blackPlastic')}
            >
              Black
            </button>
            
            <button
              className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg text-btn transition-all duration-300 ${
                selectedCard === 'appStyle'
                  ? 'bg-white text-primary-blue shadow-md'
                  : 'text-gray-600 hover:text-dark'
              }`}
              onClick={() => setSelectedCard('appStyle')}
            >
              Gradient
            </button>
          </div>
        </div>
        
        {/* Card Display */}
        <div 
          key={selectedCard}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-start lg:items-center mb-12 sm:mb-16 md:mb-24"
        >
          {/* Card Image */}
          <div className="relative order-1 lg:order-1">
            <div className="relative">
              <div className={`absolute inset-0 bg-gradient-to-br ${cards[selectedCard].gradient} opacity-20 blur-3xl`}></div>
              
              <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl overflow-hidden p-4 sm:p-6 md:p-8">
                <div className="relative h-48 sm:h-64 md:h-80 lg:h-96 flex items-center justify-center">
                  <div className={`absolute inset-0 bg-gradient-to-br ${cards[selectedCard].gradient} rounded-xl sm:rounded-2xl transform rotate-3 scale-105 opacity-20`}></div>
                  <img 
                    src={cards[selectedCard].image}
                    alt={cards[selectedCard].title}
                    className="relative z-10 w-full h-full object-contain"
                    style={{ filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15))' }}
                  />
                  
                  <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon="wifi" className="text-white text-lg sm:text-xl md:text-2xl" />
                    </div>
                  </div>
                </div>
                
                {cards[selectedCard].popular && (
                  <div className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 bg-gradient-to-r from-pink-500 to-red-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg z-20">
               <span className="text-caption font-semibold text-white">Most Popular</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Card Details */}
          <div className="order-2 lg:order-2">
            <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4 ${
              selectedCard === 'blackPlastic' ? 'bg-black' : cards[selectedCard].accent
            } text-white text-caption font-semibold`}>
              <FontAwesomeIcon icon="award" />
              <span>Premium Quality</span>
            </div>
            
            <h3 className="text-h3 text-dark mb-2">
              {cards[selectedCard].title}
            </h3>
            
            <p className="text-body-lg text-gray-600 mb-6 sm:mb-8">
              {cards[selectedCard].description}
            </p>
            
            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
              {cards[selectedCard].features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl"
                >
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                    selectedCard === 'blackPlastic' ? 'bg-black' : cards[selectedCard].accent
                  } text-white flex-shrink-0`}>
                    <FontAwesomeIcon icon={feature.icon} className="text-xs sm:text-sm" />
                  </div>
                  <span className="text-body-sm font-bold text-dark">{feature.text}</span>
                </div>
              ))}
            </div>
            
            {/* Pricing */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4">
                <div>
                  <span className="text-h3 text-dark">£{cards[selectedCard].price}</span>
                  <span className="text-body-lg text-gray-500 line-through ml-2 sm:ml-3">£{cards[selectedCard].originalPrice}</span>
                </div>
                <div className="bg-green-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-caption font-semibold">
                  {cards[selectedCard].savings}
                </div>
              </div>
              
              {/* Quantity */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-body font-semibold text-dark">Quantity:</span>
                <div className="flex items-center gap-2 bg-white rounded-lg p-1">
                  <button
                    onClick={() => handleQuantityChange(selectedCard, quantities[selectedCard] - 1)}
                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-800"
                  >
                    <FontAwesomeIcon icon="minus" className="text-xs" />
                  </button>
                  <span className="w-10 text-center text-body font-semibold">{quantities[selectedCard]}</span>
                  <button
                    onClick={() => handleQuantityChange(selectedCard, quantities[selectedCard] + 1)}
                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-800"
                  >
                    <FontAwesomeIcon icon="plus" className="text-xs" />
                  </button>
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button 
                  onClick={() => handleAddToCart(selectedCard)}
                  className="btn btn-primary flex-1"
                >
                  <FontAwesomeIcon icon="shopping-cart" />
                  <span>Add to Cart</span>
                </button>
                
                <button 
                  onClick={() => handleBuyNow(selectedCard)}
                  className="btn btn-outline flex-1"
                >
                  <FontAwesomeIcon icon="bolt" />
                  <span>Buy Now</span>
                </button>
              </div>
              
              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-4 sm:gap-6 mt-3 sm:mt-4 text-body-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon icon="truck" className="text-primary-blue" />
                  <span>Free Shipping</span>
                </div>
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon icon="shield-alt" className="text-green-500" />
                  <span>Secure Payment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NFCCards;